import { ServiceType } from '@wix/ambassador-bookings-services-v2-service/types';
import { useSettings } from '@wix/tpa-settings/react';
import {
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import React, { FC, memo, useCallback } from 'react';
import { Divider as TPADivider } from 'wix-ui-tpa/cssVars';
import { EnrichedService, ClickTarget } from '../../../../../../types/types';
import { ReferralInfo } from '../../../../../../utils/bi/consts';
import { generateServiceInfoDetails } from '../../../../../../utils/serviceDetails/displayOptions';
import { useAddVisibilityClass } from '../../../../hooks/useAddVisibilityClass';
import { useVisibilityCheck } from '../../../../hooks/useVisibilityCheck';
import settingsParams from '../../../../settingsParams';
import { useServiceInfoLayout } from '../../../hooks/useServiceInfoLayout';
import { useWidgetActions } from '../../../hooks/useWidgetActions';
import { useWidgetViewModel } from '../../../hooks/useWidgetViewModel';
import { SERVICE_INFO_LABEL_ID } from '../../consts';
import {
  BookButton as BookButtonComponent,
  BookButtonProps,
} from './BookButton/BookButton';
import {
  DataHooks,
  ServiceDetailsType,
  ServiceInfoLayoutOption,
} from './consts';
import { CourseAvailability } from './CourseAvailability/CourseAvailability';
import { Details } from './Details/Details';
import { DiscountName } from './DiscountName/DiscountName';
import { ExplorePlans } from './ExplorePlans/ExplorePlans';
import { MoreInfoButton as MoreInfoButtonComponent } from './MoreInfoButton/MoreInfoButton';
import { OnlineBadge as OnlineBadgeComponent } from './OnlineBadge/OnlineBadge';
import { classes } from './ServiceInfo.st.css';
import { TagLine } from './TagLine/TagLine';
import { Title } from './Title/Title';
import {
  BOOK_BUTTON_DESTINATION_EXPERIMENT,
  SHOW_DISCOUNT_EXPERIMENT,
} from '../../../../../../consts';
import {
  isServiceWithVariedPrice,
  isServiceWithAddOns,
} from '@wix/bookings-calendar-catalog-viewer-mapper';

export type ServiceInfoProps = {
  service: EnrichedService;
  statusInfo?: React.ReactNode;
  availability?: React.ReactNode;
  hrefUrl?: string;
  shouldNavigateImmediately: boolean;
};

export const ServiceInfo: FC<ServiceInfoProps> = ({
  service,
  shouldNavigateImmediately,
  hrefUrl,
}) => {
  const serviceInfoLayout = useServiceInfoLayout();
  const { businessInfo, coursesAvailability, ctaLoadingServiceId } =
    useWidgetViewModel();
  const { language } = useEnvironment();
  const { t } = useTranslation();
  const { experiments } = useExperiments();
  const { shouldBeVisible } = useVisibilityCheck();
  const { onServiceClick, setCtaLoadingService } = useWidgetActions();

  const isShowDiscountEnabled = experiments.enabled(SHOW_DISCOUNT_EXPERIMENT);
  const isTagLineVisible = shouldBeVisible('isTagLineVisible');
  const isServiceOfferedDaysVisible = shouldBeVisible(
    'isServiceOfferedDaysVisible',
  );
  const isServiceStartDateVisible = shouldBeVisible(
    'isServiceStartDateVisible',
  );
  const isServiceDurationVisible = shouldBeVisible('isServiceDurationVisible');
  const isServicePriceVisible = shouldBeVisible('isServicePriceVisible');

  const displayOptions = generateServiceInfoDetails({
    service,
    displayOptions: {
      isServiceOfferedDaysVisible,
      isServiceStartDateVisible,
      isServicePriceVisible,
      isServiceDurationVisible,
      isTagLineVisible,
    },
    businessInfo,
    language,
    t,
    coursesAvailability,
  });

  const {
    tagLine,
    offeredDays,
    duration,
    price,
    isPassedEndDate,
    isTooLateToBook,
    numberOfSpotsLeft,
    startDate,
  } = displayOptions;

  const titleTabIndex = useTitleTabIndex(service);
  const isCourseType = service.type === ServiceType.COURSE;
  const courseStatus = isServiceStartDateVisible ? (
    startDate ? (
      <StartDate
        startDate={startDate}
        locale={businessInfo.language}
        ellipsis={serviceInfoLayout === ServiceInfoLayoutOption.HORIZONTAL}
      />
    ) : (
      <Ended isEnded={!!isPassedEndDate} />
    )
  ) : null;

  return (
    <div
      data-id={service.id}
      data-hook={DataHooks.ROOT}
      aria-labelledby={SERVICE_INFO_LABEL_ID}
      role="group"
      className={classes.root}
    >
      <div className={classes.details}>
        <div className={classes.titleArea}>
          <Title
            tabIndex={titleTabIndex}
            ariaDescribedBy={tagLine ? `${service.id}-tagLine` : undefined}
            title={service.name!}
            url={hrefUrl}
            id={service.id!}
            onClick={() => {
              if (shouldNavigateImmediately) {
                setCtaLoadingService(service.id!);
              }
              void onServiceClick({
                service,
                referralInfo: ReferralInfo.SERVICE_TITLE,
              });
            }}
          />
          <OnlineBadge isOfferedOnline={service.conferencing?.enabled!} />
          {tagLine ? (
            <TagLine id={`${service.id}-tagLine`} tagLine={tagLine} />
          ) : null}
          <MoreInfoButton service={service} />
          <Divider />
        </div>
        <div className={classes.priceArea}>
          {offeredDays ? <OfferedDays offeredDays={offeredDays} /> : null}
          {courseStatus}
          {duration ? <Duration duration={duration} /> : null}
          {price?.displayedPrice ? (
            <Price
              displayedPrice={price.displayedPrice}
              srOnlyPrice={price.srOnlyPrice!}
              priceAfterDiscount={
                isShowDiscountEnabled ? price.priceAfterDiscount : undefined
              }
              discountName={
                isShowDiscountEnabled ? price.discountName : undefined
              }
              isVariedPrice={isServiceWithVariedPrice(service)}
            />
          ) : null}
          {isCourseType && (
            <CourseAvailability
              numberOfSpotsLeft={numberOfSpotsLeft}
              checkDivider={false}
              isTooLateToBook={!!isTooLateToBook}
              isPassedEndDate={!!isPassedEndDate}
              className={classes.HorizontalCourseAvailability}
            />
          )}
        </div>
        {isCourseType && (
          <CourseAvailability
            numberOfSpotsLeft={numberOfSpotsLeft}
            checkDivider={true}
            isTooLateToBook={!!isTooLateToBook}
            isPassedEndDate={!!isPassedEndDate}
            className={classes.VerticalCourseAvailability}
          />
        )}
      </div>
      <ServiceActions
        isTooLateToBook={isTooLateToBook}
        numberOfSpotsLeft={numberOfSpotsLeft}
        service={service}
        isLoading={ctaLoadingServiceId === service.id}
        shouldNavigateImmediately={shouldNavigateImmediately}
      />
    </div>
  );
};

const OnlineBadge = ({ isOfferedOnline }: { isOfferedOnline?: Boolean }) => {
  const { shouldBeVisible } = useVisibilityCheck();
  const showBadge = isOfferedOnline && shouldBeVisible('isOnlineBadgeVisible');
  return showBadge ? <OnlineBadgeComponent /> : null;
};

const MoreInfoButton: FC<Pick<ServiceInfoProps, 'service'>> = ({ service }) => {
  const { shouldBeVisible } = useVisibilityCheck();
  const { onMoreInfoClick } = useWidgetActions();
  return shouldBeVisible('isMoreInfoButtonVisible') ? (
    <MoreInfoButtonComponent
      url={service.urls?.servicePage?.url!}
      onClick={() =>
        onMoreInfoClick({
          service,
          referralInfo: ReferralInfo.MORE_INFO_BUTTON,
        })
      }
    />
  ) : null;
};

const Divider = () => {
  const { shouldBeVisible } = useVisibilityCheck();
  const { addVisibilityClass } = useAddVisibilityClass();
  return shouldBeVisible('isServiceDividerVisible') ? (
    <TPADivider
      data-hook={DataHooks.DIVIDER}
      className={addVisibilityClass(
        classes.divider,
        classes.dividerVisibility,
        'isServiceDividerVisible',
      )}
    />
  ) : null;
};

const OfferedDays = ({
  offeredDays,
  ellipsis = false,
}: {
  offeredDays: string;
  ellipsis?: boolean;
}) => {
  const { addVisibilityClass } = useAddVisibilityClass();
  return (
    <Details
      type={ServiceDetailsType.OFFERED_DAYS}
      ellipsis={ellipsis}
      className={addVisibilityClass(
        '',
        classes.offeredDaysVisibility,
        'isServiceOfferedDaysVisible',
      )}
    >
      {offeredDays}
    </Details>
  );
};

const StartDate = ({
  startDate,
  ellipsis = false,
}: {
  startDate: string;
  locale?: string;
  ellipsis?: boolean;
}) => {
  const { addVisibilityClass } = useAddVisibilityClass();

  return (
    <Details
      type={ServiceDetailsType.START_DATE}
      ellipsis={ellipsis}
      className={addVisibilityClass(
        '',
        classes.startDateVisibility,
        'isServiceStartDateVisible',
      )}
    >
      {startDate}
    </Details>
  );
};

const Ended = ({ isEnded }: { isEnded: boolean }) => {
  const { t } = useTranslation();
  const { addVisibilityClass } = useAddVisibilityClass();
  if (!isEnded) {
    return null;
  }
  return (
    <Details
      className={addVisibilityClass(
        '',
        classes.startDateVisibility,
        'isServiceStartDateVisible',
      )}
    >
      {t('service.schedule.course.end.date.passed')}
    </Details>
  );
};

const Duration = ({
  duration,
  ellipsis = false,
}: {
  duration: string;
  ellipsis?: boolean;
}) => {
  const { addVisibilityClass } = useAddVisibilityClass();
  return (
    <Details
      type={ServiceDetailsType.DURATION}
      ellipsis={ellipsis}
      className={addVisibilityClass(
        '',
        classes.durationVisibility,
        'isServiceDurationVisible',
      )}
    >
      {duration}
    </Details>
  );
};

const Price = ({
  displayedPrice,
  srOnlyPrice,
  priceAfterDiscount,
  discountName,
  isVariedPrice,
}: {
  displayedPrice: string;
  srOnlyPrice: string;
  priceAfterDiscount?: string;
  discountName?: string;
  isVariedPrice?: boolean;
}) => {
  const { addVisibilityClass } = useAddVisibilityClass();
  const showPriceAfterDiscount = !!priceAfterDiscount && !isVariedPrice;
  const hasDiscountInfo = showPriceAfterDiscount || !!discountName;

  return (
    <>
      <div
        data-hook={DataHooks.SR_ONLY_PRICE}
        className={addVisibilityClass(
          classes.srOnly,
          classes.priceVisibility,
          'isServicePriceVisible',
        )}
      >
        {srOnlyPrice}
      </div>
      {hasDiscountInfo ? (
        <div
          className={addVisibilityClass(
            classes.priceContainer,
            classes.priceVisibility,
            'isServicePriceVisible',
          )}
        >
          <div className={classes.pricesRow}>
            <Details
              type={ServiceDetailsType.PRICE}
              ariaHidden
              className={
                showPriceAfterDiscount
                  ? addVisibilityClass(
                      classes.originalPrice,
                      classes.priceAfterDiscountVisibility,
                      'isPriceAfterDiscountVisible',
                    )
                  : undefined
              }
            >
              {displayedPrice}
            </Details>
            {showPriceAfterDiscount && (
              <Details type={ServiceDetailsType.PRICE} ariaHidden>
                {priceAfterDiscount}
              </Details>
            )}
          </div>
          <DiscountName
            discountName={discountName}
            showTooltip={isVariedPrice}
          />
        </div>
      ) : (
        <Details
          type={ServiceDetailsType.PRICE}
          ariaHidden
          className={addVisibilityClass(
            '',
            classes.priceVisibility,
            'isServicePriceVisible',
          )}
        >
          {displayedPrice}
        </Details>
      )}
    </>
  );
};

const BookButton: FC<BookButtonProps> = memo((props) => {
  const { shouldBeVisible } = useVisibilityCheck();

  return shouldBeVisible('isBookButtonVisible') ? (
    <BookButtonComponent {...props} />
  ) : null;
});

const ServiceActions = ({
  service,
  isTooLateToBook,
  numberOfSpotsLeft,
  isLoading,
  shouldNavigateImmediately,
}: {
  isTooLateToBook?: boolean;
  numberOfSpotsLeft?: number;
  service: EnrichedService;
  isLoading: boolean;
  shouldNavigateImmediately: boolean;
}) => {
  const { onActionButtonClick, setCtaLoadingService } = useWidgetActions();
  const { addVisibilityClass } = useAddVisibilityClass();
  const { shouldBeVisible } = useVisibilityCheck();
  const settings = useSettings();
  const { experiments } = useExperiments();
  const showViewCourse = isTooLateToBook || numberOfSpotsLeft! <= 0;
  const onBookButtonClick = useCallback(() => {
    if (shouldNavigateImmediately) {
      setCtaLoadingService(service.id!);
    }

    void onActionButtonClick({
      service,
      referralInfo: ReferralInfo.BOOK_BUTTON,
    });
  }, [service]);

  const servicePageUrl = service.urls?.servicePage?.url!;
  const calendarPageUrl = service.urls?.calendarPage?.url!;
  const isBookButtonDestinationEnabled = experiments.enabled(
    BOOK_BUTTON_DESTINATION_EXPERIMENT,
  );
  const shouldNavigateToServicePage =
    (isBookButtonDestinationEnabled &&
      settings.get(settingsParams.bookButtonClickTarget) ===
        ClickTarget.SERVICE_PAGE) ||
    service.type === ServiceType.COURSE ||
    service.displayOnlyNoBookFlow;

  return shouldBeVisible('isBookButtonVisible') ? (
    <div
      className={addVisibilityClass(
        classes.button,
        classes.buttonVisibility,
        'isBookButtonVisible',
      )}
    >
      <BookButton
        onClick={onBookButtonClick}
        url={
          shouldNavigateImmediately
            ? shouldNavigateToServicePage
              ? servicePageUrl
              : calendarPageUrl
            : undefined
        }
        loading={isLoading}
        isNoBookFlow={service.displayOnlyNoBookFlow}
        isPendingApprovalFlow={service.onlineBooking?.requireManualApproval!}
        showViewCourse={showViewCourse}
        serviceId={service.id!}
      />
      <ExplorePlans service={service} />
    </div>
  ) : (
    <ExplorePlans service={service} onlyLink />
  );
};

const useTitleTabIndex = (service: EnrichedService) => {
  const settings = useSettings();
  const { shouldBeVisible } = useVisibilityCheck();
  return service.displayOnlyNoBookFlow ||
    service.type === ServiceType.COURSE ||
    settings.get(settingsParams.serviceClickTarget) === ClickTarget.SERVICE_PAGE
    ? shouldBeVisible('isMoreInfoButtonVisible')
      ? -1
      : 0
    : shouldBeVisible('isBookButtonVisible')
    ? -1
    : 0;
};
